// Колоните на „Продажби по периоди" — клиентски режим: отговорът носи целия
// период наведнъж, затова сортирането, филтрите и редът „Общо" са в браузъра.
//
// Видимите по подразбиране са тези, по които човек чете справката (период,
// сметки, количество, сума, сума ФП, ДДС, нето, средни). Останалите
// (по метод на плащане, по станция, базово по менюто, отстъпки, надбавки,
// клиенти) стоят в панела „Колони" и се палят с една отметка.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import type { TextCellValue } from "../../../../components/dataTable";
import type { MethodEntry } from "../../../../utils/paymentMethodsDisplay";
import type { PeriodRow } from "./salesByPeriodsMath";

const helper = createColumnHelper<PeriodRow>();

/** Станция, която става колона — името е на езика на интерфейса. */
export type StationColumn = { id: number; label: string };

type Options = {
  /** Методите на периода — по една изгасена колона на метод. */
  methodEntries: MethodEntry[];
  /** Станциите — по една изгасена колона с оборота на станцията. */
  stationColumns: StationColumn[];
};

export const useSalesByPeriodsColumns = ({
  methodEntries,
  stationColumns,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      helper.accessor(
        (row): TextCellValue => ({ primary: row.label, secondary: row.sublabel }),
        {
          id: "period",
          header: t("reportsPage.salesPeriodsPeriodColumn"),
          size: 190,
          // Хронологично по ключа на BE, не по показания текст — иначе
          // „понеделник" застава след „вторник" по азбучен ред.
          sortingFn: (rowA, rowB) => rowA.original.key.localeCompare(rowB.original.key),
          meta: {
            cell: { kind: "text", lines: 2 },
            filter: { kind: "text" },
            alwaysVisible: true,
            mobileRole: "title",
            excel: { format: "text" },
          },
        },
      ),

      helper.accessor((row) => row.bills, {
        id: "bills",
        header: t("reportsPage.salesPeriodsBillsColumn"),
        size: 96,
        meta: {
          cell: { kind: "num" },
          filter: { kind: "numberRange" },
          total: "sum",
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.quantity, {
        id: "quantity",
        header: t("reportsPage.salesPeriodsQuantityColumn"),
        size: 104,
        meta: {
          cell: { kind: "num" },
          filter: { kind: "numberRange" },
          total: "sum",
          hideBelow: "tablet",
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.revenue, {
        id: "revenue",
        header: t("reportsPage.salesPeriodsRevenueColumn"),
        size: 128,
        meta: {
          cell: { kind: "money", strong: true },
          filter: { kind: "numberRange" },
          total: "sum",
          alwaysVisible: true,
          mobileRole: "strong",
          excel: { format: "money" },
        },
      }),

      // „Сума ФП" — частта от сумата по сметки с касова бележка; стои до
      // „Сума", за да се сверява фискализираното с един поглед.
      helper.accessor((row) => row.fiscalized, {
        id: "fiscalized",
        header: t("reportsPage.salesPeriodsFiscalizedColumn"),
        size: 130,
        meta: {
          cell: { kind: "money", mutedZero: true },
          filter: { kind: "numberRange" },
          total: "sum",
          hideBelow: "tablet",
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.vatAmount, {
        id: "vat",
        header: t("reportsPage.salesPeriodsVatColumn"),
        size: 110,
        meta: {
          cell: { kind: "money", mutedZero: true },
          filter: { kind: "numberRange" },
          total: "sum",
          hideBelow: "tablet",
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.netAmount, {
        id: "net",
        header: t("reportsPage.salesPeriodsNetColumn"),
        size: 118,
        meta: {
          cell: { kind: "money" },
          filter: { kind: "numberRange" },
          total: "sum",
          hideBelow: "tablet",
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.avgPrice, {
        id: "avgPrice",
        header: t("reportsPage.salesPeriodsAvgPriceColumn"),
        size: 118,
        meta: {
          cell: { kind: "money" },
          filter: { kind: "numberRange" },
          hideBelow: "narrow",
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.avgBill, {
        id: "avgBill",
        header: t("reportsPage.salesPeriodsAvgBillColumn"),
        size: 124,
        meta: {
          cell: { kind: "money" },
          filter: { kind: "numberRange" },
          hideBelow: "narrow",
          excel: { format: "money" },
        },
      }),

      // Клиентите (сметки с `customer_id` от модула „Отстъпки"): брой
      // различни, оборотът по техните сметки и средното на клиент.
      helper.accessor((row) => row.clients, {
        id: "clients",
        header: t("reportsPage.salesPeriodsClientsColumn"),
        size: 110,
        meta: {
          cell: { kind: "num" },
          filter: { kind: "numberRange" },
          total: "sum",
          defaultHidden: true,
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.clientRevenue, {
        id: "clientRevenue",
        header: t("reportsPage.salesPeriodsClientRevenueColumn"),
        size: 120,
        meta: {
          cell: { kind: "money", mutedZero: true },
          filter: { kind: "numberRange" },
          total: "sum",
          defaultHidden: true,
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.clientAvg, {
        id: "clientAvg",
        header: t("reportsPage.salesPeriodsClientAvgColumn"),
        size: 140,
        meta: {
          cell: { kind: "money", mutedZero: true },
          filter: { kind: "numberRange" },
          defaultHidden: true,
          excel: { format: "money" },
        },
      }),

      // По една колона на метод на плащане; смесеното е разпределено в
      // „В брой" и „С карта" (своя колона няма), както навсякъде в справките.
      ...methodEntries.map((entry) =>
        helper.accessor((row) => row.methodAmounts.get(entry.id) ?? 0, {
          id: `method-${entry.id}`,
          header: entry.label,
          size: 140,
          meta: {
            cell: { kind: "money", mutedZero: true },
            total: "sum",
            defaultHidden: true,
            excel: { format: "money" },
          },
        }),
      ),

      // По една колона на станция: оборотът на станцията в отрязъка
      // (продуктът — при основната си станция, добавката — при своята).
      ...stationColumns.map((station) =>
        helper.accessor((row) => row.stationAmounts.get(station.id) ?? 0, {
          id: `station-${station.id}`,
          header: station.label,
          size: 140,
          meta: {
            cell: { kind: "money", mutedZero: true },
            total: "sum",
            defaultHidden: true,
            excel: { format: "money" },
          },
        }),
      ),

      helper.accessor((row) => row.baseAmount, {
        id: "baseAmount",
        header: t("reportsPage.salesPeriodsBaseColumn"),
        size: 130,
        meta: {
          cell: { kind: "money", mutedZero: true },
          filter: { kind: "numberRange" },
          total: "sum",
          defaultHidden: true,
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.discount, {
        id: "discount",
        header: t("reportsPage.salesPeriodsDiscountColumn"),
        size: 120,
        meta: {
          cell: { kind: "money", mutedZero: true },
          filter: { kind: "numberRange" },
          total: "sum",
          defaultHidden: true,
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.markup, {
        id: "markup",
        header: t("reportsPage.salesPeriodsMarkupColumn"),
        size: 120,
        meta: {
          cell: { kind: "money", mutedZero: true },
          filter: { kind: "numberRange" },
          total: "sum",
          defaultHidden: true,
          excel: { format: "money" },
        },
      }),
    ],
    [t, methodEntries, stationColumns],
  );
};
